/**
 * PointLineGroup Component
 * 
 * Renders measurement points and connecting lines in 3D space.
 * Handles color conversion and visibility management for measurement visualizations.
 * Creates a connected path through all points plus a closing line to form a complete shape.
 */

import React from "react";
import { Vector3 } from "three";
import PointsLine from "./points-line";

/**
 * Props for the PointLineGroup component
 */
interface PointLineGroupProps {
  /** Array of 3D points to connect with lines */
  points: Vector3[];
  /** Whether the point-line group should be visible */
  visible: boolean;
  /** Deprecated: Use stringLineColor or lineColor instead */
  color?: string;
  /** Line color as a hexadecimal number */
  lineColor?: number;
  /** Line color as a hex string (e.g., "#00FF00") */
  stringLineColor?: string;
}

const PointLineGroup: React.FC<PointLineGroupProps> = ({
  points,
  visible,
  color,
  lineColor,
  stringLineColor,
}) => {
  // Handle color conversion from string format to numeric format
  let processedLineColor = lineColor;
  let processedColor = color;

  if (stringLineColor) {
    // Convert hex string color (e.g., "#00FF00") to numeric format
    if (stringLineColor.length === 7 && stringLineColor.charAt(0) === "#") {
      processedLineColor = parseInt(stringLineColor.substring(1), 16);
      processedColor = "black"; // Default point color when using string line color
    } else {
      console.warn("Invalid string line color value. Expected format is #RRGGBB");
    }
  }

  /**
   * Creates connected lines between consecutive points
   * @returns Array of PointsLine components
   */
  const createLines = () => {
    return points.map((point: Vector3, index: number) => (
      <PointsLine
        key={`line-${index}`}
        start={point}
        end={index === points.length - 1 ? undefined : points[index + 1]}
        visible={visible}
        lineColor={processedLineColor}
        color={processedColor}
      />
    ));
  };

  return (
    <>
      {/* Render connecting lines between consecutive points */}
      {createLines()}
      
      {/* Render closing line from last point back to first point (if more than 1 point) */}
      {points.length > 1 && (
        <PointsLine
          key="closing-line"
          start={points[0]}
          end={points[points.length - 1]}
          visible={visible}
          lineColor={processedLineColor}
          color={processedColor}
        />
      )}
    </>
  );
};

export default PointLineGroup;